/* Catálogo de mapas/pistas. Carrega /maps/catalog.json em runtime.
 * A pista em si continua procedural (anel); o catálogo define parâmetros + cenário opcional. */
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { TRACK as DEFAULT_TRACK, TAU } from '../Race/Track.js'

export const DEFAULT_MAP = 'classic'

const BUILTIN = [
    {
        id: 'classic',
        name: 'Pista Clássica',
        owner: 'Carpano',
        radius: 50,
        bandMin: 38,
        bandMax: 62,
        startZ: 11.5,
        grassColor: '#4a9c3f',
        asphaltColor: '#3a3a3a',
        laps: 3,
    },
]

function parseColor(v, fallback) {
    if (typeof v === 'number') return v
    if (typeof v === 'string') {
        const n = parseInt(v.replace('#', ''), 16)
        if (!Number.isNaN(n)) return n
    }
    return fallback
}

class MapCatalog {
    constructor() {
        this.maps = new Map()
        BUILTIN.forEach((m) => this.maps.set(m.id, m))
        this.glbCache = new Map()
        this.texCache = new Map()
        this.loaded = false
        this._gltf = null
        this.currentId = DEFAULT_MAP
        this.active = this._toRuntime(this.maps.get(DEFAULT_MAP))
    }

    async load(url = '/maps/catalog.json') {
        try {
            const res = await fetch(url, { cache: 'no-store' })
            if (res.ok) {
                const data = await res.json()
                for (const m of data.maps || []) {
                    if (m && typeof m.id === 'string' && /^[\w-]{1,32}$/.test(m.id)) {
                        this.maps.set(m.id, { name: m.id, owner: '', ...m })
                    }
                }
            }
        } catch (_) { /* só builtins */ }
        this.loaded = true
        // reaplica o mapa ativo (pode ter sido sobrescrito pelo JSON)
        if (this.maps.has(this.currentId)) {
            this.active = this._toRuntime(this.maps.get(this.currentId))
        }
        return this.list()
    }

    list() { return [...this.maps.values()] }
    has(id) { return this.maps.has(id) }
    get(id) { return this.maps.get(id) || this.maps.get(DEFAULT_MAP) }

    /** Runtime shape usado por Race/Floor */
    _toRuntime(m) {
        const radius = m.radius ?? DEFAULT_TRACK.radius
        const bandMin = m.bandMin ?? radius - 12
        const bandMax = m.bandMax ?? radius + 12
        const startZ = m.startZ ?? 11.5
        const startAngle = m.startAngle != null ? m.startAngle : Math.atan2(startZ, radius)
        return {
            id: m.id,
            name: m.name,
            radius,
            bandMin,
            bandMax,
            startAngle,
            startZ,
            laps: m.laps ?? 3,
            grassColor: parseColor(m.grassColor, 0x4a9c3f),
            asphaltColor: parseColor(m.asphaltColor, 0x3a3a3a),
            fenceColor: parseColor(m.fenceColor, 0x888888),
            skyColor: parseColor(m.skyColor, 0x87ceeb),
            floorTexture: m.floorTexture || null,
            scenery: m.scenery || null,
            sceneryScale: m.sceneryScale ?? 1,
            sceneryY: m.sceneryY ?? 0,
            raw: m,
        }
    }

    /** Troca o mapa ativo (só parâmetros; quem chama aplica visual). */
    setActive(id) {
        const m = this.get(id)
        this.currentId = m.id
        this.active = this._toRuntime(m)
        return this.active
    }

    /** Snapshot compatível com TRACK antigo (radius, startAngle, bandMin, bandMax) */
    getTrackParams() {
        const a = this.active
        return {
            radius: a.radius,
            startAngle: a.startAngle,
            bandMin: a.bandMin,
            bandMax: a.bandMax,
        }
    }

    _loader() {
        if (!this._gltf) {
            const draco = new DRACOLoader()
            draco.setDecoderPath('/draco/')
            this._gltf = new GLTFLoader()
            this._gltf.setDRACOLoader(draco)
        }
        return this._gltf
    }

    async loadScenery(path) {
        if (!path) return null
        if (!this.glbCache.has(path)) {
            this.glbCache.set(path, new Promise((resolve, reject) =>
                this._loader().load(path, (g) => resolve(g.scene), undefined, reject)))
        }
        try {
            const scene = await this.glbCache.get(path)
            return scene.clone(true)
        } catch (err) {
            console.warn('[maps] cenário falhou', path, err)
            return null
        }
    }

    loadFloorTexture(url) {
        if (!url) return null
        if (!this.texCache.has(url)) {
            const loader = new THREE.TextureLoader()
            const t = loader.load(url)
            t.colorSpace = THREE.SRGBColorSpace
            t.wrapS = t.wrapT = THREE.RepeatWrapping
            t.repeat.set(8, 8)
            this.texCache.set(url, t)
        }
        return this.texCache.get(url)
    }
}

export default new MapCatalog()
